import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getActivityLog } from "../../../redux/slices/ActivityLogSlice";
import { formatPayloadValue, DEPOSIT_PAYLOAD_ORDER } from "../../../helpers/activityLogHelpers";
import ActivityLogTimelineView from "../../../components/ActivityLog/ActivityLogTimelineView";

const InvestorActivityLog = ({ orgId, investorName }) => {
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const limit = 20;

  const { logs = [], loading = false, totalRecords = 0 } = useSelector((state) => state?.activityLog) || {};


  const filterPayloadEntries = (payload = {}) => {
    if (!payload || typeof payload !== "object") return [];
    const keys = Object.keys(payload);
    const hasRmName = keys.some(k => k === 'rmName' || k === 'assignedRmName');
    const isDepositPayload = Boolean(payload.transactionId && (payload.purpose || payload.virtualAccountNumber));

    const isVisible = (key, value) => {
      const lowerKey = key.toLowerCase().replace(/[^a-z0-9]/g, '');

      if (
        lowerKey === 'previousstatus' ||
        lowerKey === 'updatedstatus' ||
        lowerKey === 'issuspicious' ||
        lowerKey === 'isinvestor' ||
        lowerKey === 'dealid' ||
        lowerKey === 'txnchannel' ||
        lowerKey === 'businessname'
      ) {
        return false;
      }
      if (value === null || value === undefined || value === '' || value === 'N/A') {
        return false;
      }

      // 1. Remove Investor User ID
      if (
        lowerKey === 'investoruserid' ||
        lowerKey === 'invuserid' ||
        lowerKey === 'investorid' ||
        lowerKey === 'investoruser'
      ) {
        return false;
      }

      // 2. Remove Assignee User ID
      if (
        lowerKey === 'assigneeuserid' ||
        lowerKey === 'assigneduserid' ||
        lowerKey === 'assigneeid' ||
        lowerKey === 'assignedto'
      ) {
        return false;
      }

      // 3. Remove Entity / Business Type IDs
      if (
        lowerKey === 'entitytypeid' ||
        lowerKey === 'entitytype' ||
        lowerKey === 'businesstypeid' ||
        lowerKey === 'businesstype' ||
        lowerKey === 'investortypeid' ||
        lowerKey === 'usertypeid'
      ) {
        return false;
      }

      // 4. If rmName is present, hide raw rmUserId/rmId
      if (
        hasRmName &&
        (lowerKey === 'rmuserid' || lowerKey === 'rmid' || lowerKey === 'assignedrmuserid' || lowerKey === 'assignedrmid')
      ) {
        return false;
      }

      return true;
    };

    if (isDepositPayload) {
      return DEPOSIT_PAYLOAD_ORDER
        .filter((key) => Object.prototype.hasOwnProperty.call(payload, key) && isVisible(key, payload[key]))
        .map((key) => [key, payload[key]]);
    }

    return Object.entries(payload).filter(([key, value]) => isVisible(key, value));
  };

  const formatPayloadKey = (key) => {
    if (key === 'dealCategoryId') return 'Deal Category Name';
    if (key === 'busName' || key === 'businessName') return 'Investor Name';
    if (
      key === 'investorTypeName' ||
      key === 'investorType' ||
      key === 'investorTypeId' ||
      key === 'businessTypeName' ||
      key === 'businessType' ||
      key === 'businesstype' ||
      key === 'businesstypeid'
    ) return 'Investor Type Name';
    if (key === 'rmName' || key === 'assignedRmName' || key === 'rmUserId' || key === 'rmId' || key === 'assignedRmUserId') return 'RM Name';
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
  };

  useEffect(() => {
    if (orgId) {
      dispatch(getActivityLog({ module: "INVESTOR", entityId: orgId, page, limit }));
    }
  }, [dispatch, orgId, page]);

  const getActionConfig = (action) => {
    switch (action) {
      case "NEW_INVESTOR":
      case "INVESTOR_REGISTERED": return { label: "Onboarding", color: "#e6ffed", textColor: "#22c55e" };
      case "INVESTOR_APPROVED": return { label: "Approved", color: "#e6ffed", textColor: "#22c55e" };
      case "INVESTOR_REJECTED": return { label: "Rejected", color: "#fff1f0", textColor: "#f5222d" };
      case "INVESTOR_KYC_COMPLETED": return { label: "KYC Completed", color: "#e6ffed", textColor: "#22c55e" };
      case "INVESTOR_SLA_SIGNED": return { label: "SLA Signed", color: "#e6ffed", textColor: "#22c55e" };
      case "BOARD_RESOLUTION_SUBMITTED": return { label: "Board Resolution Submitted", color: "#e6f7ff", textColor: "#1890ff" };
      case "MANUAL_KYC_APPROVED": return { label: "KYC Step Approved", color: "#e6ffed", textColor: "#22c55e" };
      case "MANUAL_KYC_REJECTED": return { label: "KYC Step Rejected", color: "#fff1f0", textColor: "#f5222d" };
      case "MANUAL_KYC_SUBMITTED": return { label: "KYC Submitted", color: "#e6f7ff", textColor: "#1890ff" };
      case "INVESTOR_PROFILE_UPDATED":
      case "BUSINESS_PROFILE_UPDATED": return { label: "Profile Update", color: "#e6f7ff", textColor: "#1890ff" };
      case "INVESTOR_DEACTIVATED":
      case "BUSINESS_DEACTIVATED": return { label: "Deactivation", color: "#fff0f6", textColor: "#eb2f96" };
      case "INVESTOR_BLACKLISTED":
      case "BUSINESS_BLACKLISTED": return { label: "Blacklist", color: "#fff1f0", textColor: "#f5222d" };
      case "INVESTOR_DELINQUENT":
      case "BUSINESS_DELINQUENT": return { label: "Delinquent", color: "#fffbe6", textColor: "#d4b106" };
      case "INVESTOR_REACTIVATED":
      case "BUSINESS_REACTIVATED": return { label: "Reactivation", color: "#e6ffed", textColor: "#22c55e" };
      case "RM_ASSIGNED": return { label: "RM Assigned", color: "#fff2e8", textColor: "#fa541c" };
      default: return { label: action.replace(/^BUSINESS_|^INVESTOR_/, "").replace(/_/g, " "), color: "#e6f7ff", textColor: "#1890ff" };
    }
  };

  const formatDesc = (log) => {
    const role = log.performedByRole || "System";
    const name = log.performedByName || "Unknown User";
    const targetName = investorName || log.payload?.investorName || log.payload?.busName || "Investor";

    if (log.action === "NEW_INVESTOR" || log.action === "INVESTOR_REGISTERED") {
      return `<strong>${name}</strong> (${role}) <strong>REGISTERED</strong> investor <strong>"${targetName}"</strong>`;
    }
    if (log.action === "INVESTOR_APPROVED") {
      return `<strong>${name}</strong> (${role}) <strong>APPROVED</strong> investor <strong>"${targetName}"</strong>`;
    }
    if (log.action === "INVESTOR_REJECTED") {
      return `<strong>${name}</strong> (${role}) <strong>REJECTED</strong> investor <strong>"${targetName}"</strong>`;
    }
    if (log.action === "INVESTOR_KYC_COMPLETED") {
      return `<strong>${name}</strong> (${role}) <strong>COMPLETED KYC VERIFICATION</strong> for investor <strong>"${targetName}"</strong>`;
    }
    if (log.action === "INVESTOR_SLA_SIGNED") {
      return `<strong>${name}</strong> (${role}) <strong>SIGNED SLA</strong> for investor <strong>"${targetName}"</strong>`;
    }
    if (log.action === "BOARD_RESOLUTION_SUBMITTED") {
      return `<strong>${name}</strong> (${role}) <strong>SUBMITTED BOARD RESOLUTION</strong> for investor <strong>"${targetName}"</strong>`;
    }
    if (log.action === "MANUAL_KYC_APPROVED") {
      return `<strong>${name}</strong> (${role}) <strong>APPROVED KYC STEP</strong> for investor <strong>"${targetName}"</strong>`;
    }
    if (log.action === "MANUAL_KYC_REJECTED") {
      return `<strong>${name}</strong> (${role}) <strong>REJECTED KYC STEP</strong> for investor <strong>"${targetName}"</strong>`;
    }
    if (log.action === "MANUAL_KYC_SUBMITTED") {
      return `<strong>${name}</strong> (${role}) <strong>SUBMITTED KYC DOCUMENTS</strong> for investor <strong>"${targetName}"</strong>`;
    }
    if (log.action === "RM_ASSIGNED") {
      const rm = log.payload?.rmName || "Relationship Manager";
      return `<strong>${name}</strong> (${role}) <strong>ASSIGNED RELATIONSHIP MANAGER</strong> <strong>"${rm}"</strong> to <strong>"${targetName}"</strong>`;
    }
    if (log.action === "INVESTOR_DEACTIVATED" || log.action === "BUSINESS_DEACTIVATED") {
      return `<strong>${name}</strong> (${role}) <strong>DEACTIVATED</strong> investor <strong>"${targetName}"</strong>`;
    }
    if (log.action === "INVESTOR_BLACKLISTED" || log.action === "BUSINESS_BLACKLISTED") {
      return `<strong>${name}</strong> (${role}) <strong>BLACKLISTED</strong> investor <strong>"${targetName}"</strong>`;
    }
    if (log.action === "INVESTOR_DELINQUENT" || log.action === "BUSINESS_DELINQUENT") {
      return `<strong>${name}</strong> (${role}) <strong>MARKED</strong> investor <strong>"${targetName}"</strong> as <strong>DELINQUENT</strong>`;
    }
    if (log.action === "INVESTOR_REACTIVATED" || log.action === "BUSINESS_REACTIVATED") {
      return `<strong>${name}</strong> (${role}) <strong>REACTIVATED</strong> investor <strong>"${targetName}"</strong>`;
    }
    if (log.action === "INVESTOR_PROFILE_UPDATED" || log.action === "BUSINESS_PROFILE_UPDATED") {
      return `<strong>${name}</strong> (${role}) <strong>UPDATED PROFILE</strong> for investor <strong>"${targetName}"</strong>`;
    }
    
    const actionConfig = getActionConfig(log.action);
    return `<strong>${name}</strong> (${role}) <strong>PERFORMED ${actionConfig.label.toUpperCase()}</strong> on investor <strong>"${targetName}"</strong>`;
  };

  return (
    <ActivityLogTimelineView
      title="Investor Lifecycle History"
      totalRecords={totalRecords}
      loading={loading}
      logs={logs}
      page={page}
      setPage={setPage}
      limit={limit}
      getActionConfig={getActionConfig}
      formatDesc={formatDesc}
      filterPayloadEntries={filterPayloadEntries}
      formatPayloadKey={formatPayloadKey}
      formatPayloadValue={formatPayloadValue}
    />
  );
};

export default InvestorActivityLog;
